import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Layout,
  Toast,
  ButtonGroup,
  BlockStack,
  Text,
  List
} from '@shopify/polaris';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { openprojectService } from '../services/openprojectService';
import LoadingState from './shared/LoadingState';

const Settings = () => {
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [telegramBotToken, setTelegramBotToken] = useState('');
  const [telegramBotTokenMasked, setTelegramBotTokenMasked] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [telegramTestLoading, setTelegramTestLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [telegramTestResult, setTelegramTestResult] = useState(null);
  const { updateOpenProjectConfig } = useAuth();

  useEffect(() => {
    loadCurrentConfig();
  }, []);

  const loadCurrentConfig = async () => {
    try {
      const config = await authService.getOpenProjectConfig();
      setUrl(config.url || '');
      setApiKeyMasked(config.apiKeyMasked || '');
      setTelegramBotTokenMasked(config.telegramBotTokenMasked || '');
      setTelegramChatId(config.telegramChatId || '');
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await updateOpenProjectConfig(url, apiKey, telegramBotToken, telegramChatId);

    if (result.success) {
      setSuccess('Settings saved successfully');
      setApiKey('');
      setTelegramBotToken('');
      loadCurrentConfig();
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleTestConnection = async () => {
    if (!url || (!apiKey && !apiKeyMasked)) {
      setError('Please enter URL and API Key first');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    setError('');

    try {
      // Save first (only overwrites the API key if a new one was typed)
      const result = await updateOpenProjectConfig(url, apiKey, telegramBotToken, telegramChatId);
      if (!result.success) {
        setError(result.error);
        setTestLoading(false);
        return;
      }

      const testResponse = await openprojectService.testConnection();
      setTestResult({
        success: testResponse.success,
        message: testResponse.message,
        version: testResponse.version
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Connection failed'
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!telegramBotToken && !telegramBotTokenMasked) {
      setError('Please enter a Telegram Bot Token first');
      return;
    }
    if (!telegramChatId) {
      setError('Please enter a Telegram Chat ID first');
      return;
    }

    setTelegramTestLoading(true);
    setTelegramTestResult(null);
    setError('');

    try {
      // Only sends a token if the user typed a new one; otherwise the
      // backend falls back to the currently saved one.
      const testResponse = await authService.testTelegramConnection(telegramBotToken || undefined, telegramChatId);
      setTelegramTestResult({
        success: testResponse.success,
        message: testResponse.message
      });
    } catch (error) {
      setTelegramTestResult({
        success: false,
        message: error.response?.data?.message || 'Telegram test failed'
      });
    } finally {
      setTelegramTestLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Page title="Settings">
        <LoadingState />
      </Page>
    );
  }

  const toastMarkup = success ? <Toast content={success} onDismiss={() => setSuccess('')} /> : null;

  return (
    <Page title="Settings" subtitle="OpenProject connection and Telegram notification settings">
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="500">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  OpenProject Configuration
                </Text>
                <Text as="p" tone="subdued">
                  Enter your OpenProject URL and API key to access data
                </Text>
              </BlockStack>

              {error && <Banner tone="critical">{error}</Banner>}

              {testResult && (
                <Banner tone={testResult.success ? 'success' : 'critical'}>
                  {testResult.message}
                  {testResult.version && ` (Version: ${testResult.version})`}
                </Banner>
              )}

              {telegramTestResult && (
                <Banner tone={telegramTestResult.success ? 'success' : 'critical'}>
                  {telegramTestResult.message}
                </Banner>
              )}

              <form onSubmit={handleSubmit}>
                <FormLayout>
                  <TextField
                    label="OpenProject Server URL"
                    value={url}
                    onChange={setUrl}
                    placeholder="https://your-openproject-instance.com"
                    helpText="The full URL of your OpenProject server"
                    disabled={loading || testLoading}
                    autoComplete="off"
                  />
                  <TextField
                    label="API Key"
                    value={apiKey}
                    onChange={setApiKey}
                    placeholder={apiKeyMasked ? `Currently set (${apiKeyMasked}) - leave blank to keep` : 'Enter your OpenProject API key'}
                    helpText="Generate one from OpenProject: My account > Access tokens"
                    disabled={loading || testLoading}
                    autoComplete="off"
                  />

                  <BlockStack gap="300">
                    <BlockStack gap="100">
                      <Text as="h3" variant="headingSm">
                        Telegram Bot Configuration
                      </Text>
                      <Text as="p" tone="subdued">
                        Used to send time entry notifications
                      </Text>
                    </BlockStack>

                    <TextField
                      label="Telegram Bot Token"
                      value={telegramBotToken}
                      onChange={setTelegramBotToken}
                      placeholder={telegramBotTokenMasked ? `Currently set (${telegramBotTokenMasked}) - leave blank to keep` : 'Get this from @BotFather'}
                      disabled={loading || testLoading}
                      autoComplete="off"
                    />

                    <TextField
                      label="Telegram Chat ID"
                      value={telegramChatId}
                      onChange={setTelegramChatId}
                      placeholder="123456789"
                      helpText="The chat or group ID to post notifications to"
                      disabled={loading || testLoading || telegramTestLoading}
                      autoComplete="off"
                    />

                    <Button
                      onClick={handleTestTelegram}
                      loading={telegramTestLoading}
                      disabled={(!telegramBotToken && !telegramBotTokenMasked) || !telegramChatId || loading || testLoading}
                      variant="secondary"
                    >
                      Test Telegram Connection
                    </Button>
                  </BlockStack>

                  <ButtonGroup>
                    <Button
                      variant="primary"
                      submit
                      loading={loading}
                      disabled={!url || (!apiKey && !apiKeyMasked) || testLoading || telegramTestLoading}
                    >
                      Save Settings
                    </Button>
                    <Button
                      onClick={handleTestConnection}
                      loading={testLoading}
                      disabled={!url || (!apiKey && !apiKeyMasked) || loading || telegramTestLoading}
                    >
                      Test OpenProject Connection
                    </Button>
                  </ButtonGroup>
                </FormLayout>
              </form>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Setup Guide
              </Text>

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  OpenProject API Key
                </Text>
                <List type="number">
                  <List.Item>Log into your OpenProject account</List.Item>
                  <List.Item>Go to "My account"</List.Item>
                  <List.Item>Open the "Access tokens" tab</List.Item>
                  <List.Item>Create a new token</List.Item>
                  <List.Item>Copy the token and paste it above</List.Item>
                </List>
              </BlockStack>

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Telegram Bot Setup
                </Text>
                <List type="number">
                  <List.Item>Message @BotFather on Telegram</List.Item>
                  <List.Item>Send /newbot and follow the prompts</List.Item>
                  <List.Item>Copy the bot token (format: 123456789:ABC...)</List.Item>
                  <List.Item>Send any message to your new bot</List.Item>
                  <List.Item>
                    Get the chat ID from{' '}
                    <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
                  </List.Item>
                  <List.Item>Enter both values above and test the connection</List.Item>
                </List>
              </BlockStack>

              <Banner tone="info">
                The test button sends a sample message to your Telegram chat to verify everything works.
              </Banner>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      {toastMarkup}
    </Page>
  );
};

export default Settings;
